/*
    Transaction activity is handled on the backend (statements) + frontend, so we are overloading to make different types work in both instances
*/
import {
  Advance as GqlAdvance,
  Disbursement as GqlDisbursement,
  Draw as GqlDraw,
  FlexModification as GqlFlexModification,
  Payment as GqlPayment,
  PrincipalModification as GqlPrincipalModification,
  Writeoff as GqlWriteoff,
} from '@willow/graphql-iso/src/app';
import { TransactionActivity as TransactionActivityPortal } from '@willow/graphql-iso/src/portal';
import {
  Advance,
  Disbursement,
  Fee,
  FlexModification,
  Payment,
  PrincipalModification,
  Writeoff,
} from '@willow/types-iso';

export type TransactionActivityItem =
  | Payment
  | Disbursement
  | Fee
  | Advance
  | FlexModification
  | PrincipalModification
  | Writeoff;
export type GqlTransactionActivityItem =
  | GqlPayment
  | GqlDisbursement
  | GqlDraw
  | GqlAdvance
  | GqlFlexModification
  | GqlPrincipalModification
  | GqlWriteoff;

const getTransactionDate = (transaction: TransactionActivityItem | GqlTransactionActivityItem): Date => {
  let date;

  if ('paymentDate' in transaction) {
    // Payment type
    date = transaction.paymentDate;
  } else if ('chargeDate' in transaction) {
    // Fee type
    date = transaction.chargeDate;
  } else if ('date' in transaction) {
    // Disbursement type
    date = transaction.date;
  } else if ('effectiveDate' in transaction) {
    // PrincipalModification type
    // Writeoff type
    date = transaction.effectiveDate;
  }

  return new Date(date);
};

/**
 * Most recent first. Same-day entries keep reverse input order (later entries first), except that a
 * reversal always renders above the payment it reverses: paymentDate is day-granular on the wire, so
 * a same-day reversal ties with its original. The reversal takes at least the slot just past its
 * original, which puts it above it here. Sorting on a (date, position) key rather than a pairwise
 * "a reverses b" tie-break matters: the pairwise form is not a strict weak ordering, and
 * Array.prototype.sort can leave the pair inverted when unrelated same-day entries sit between them.
 */
export function sortTransactions<T extends TransactionActivityItem | GqlTransactionActivityItem>(
  transactions: T[],
): T[] {
  if (!transactions?.length) return [];

  const positionById = new Map<string, number>(transactions.map((transaction, index) => [transaction.id, index]));

  const position = (transaction: TransactionActivityItem | GqlTransactionActivityItem, index: number): number => {
    const originalPosition =
      'transactionId' in transaction && transaction.transactionId
        ? positionById.get(transaction.transactionId)
        : undefined;
    return originalPosition === undefined ? index : Math.max(index, originalPosition + 0.5);
  };

  return transactions
    .map((transaction, index) => ({
      transaction,
      time: getTransactionDate(transaction).getTime(),
      position: position(transaction, index),
    }))
    .sort((a, b) => b.time - a.time || b.position - a.position)
    .map(({ transaction }) => transaction);
}

interface ObjectWithDate {
  date: string;
}
export type TransactionActivityStatement = {
  date: string;
  description: string;
  amount: string;
  isCharge: boolean;
  isDeferred: boolean;
};
export function sortTransactionsActivity(transactions: TransactionActivityPortal[]): TransactionActivityPortal[];
export function sortTransactionsActivity(transactions: TransactionActivityStatement[]): TransactionActivityStatement[];
export function sortTransactionsActivity<T extends ObjectWithDate>(transactions: T[]): T[];
export function sortTransactionsActivity(
  transactions: TransactionActivityStatement[] | TransactionActivityPortal[],
): TransactionActivityStatement[] | TransactionActivityPortal[] {
  // Post-MVP this doesn't necessarily work as payment dates may have the same date and we dont have datetime stamp
  if (!transactions?.length) return [];

  const sortedByDate = transactions.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();

    // Return most recent date first
    return dateB > dateA ? 1 : -1;
  });

  return sortedByDate;
}
